import ApiService from './ApiService';

class AnalyticsService {
  private sessionId: string;
  private deviceType: string;
  private platform: string;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.deviceType = this.getDeviceType();
    this.platform = 'web';
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private getDeviceType(): string {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return 'tablet';
    }
    if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
      return 'mobile';
    }
    return 'desktop';
  }

  async track(eventType: string, eventData?: any) {
    try {
      await ApiService.post('/analytics/track', {
        eventType,
        eventData,
        sessionId: this.sessionId,
        deviceType: this.deviceType,
        platform: this.platform
      });
    } catch (error) {
      // Silently fail - don't let analytics errors break the app
      console.debug('Analytics tracking failed:', error);
    }
  }

  // Convenience methods for common events
  trackPageView(pageName: string) {
    this.track('page_view', { page: pageName });
  }

  trackSignup(method: string = 'email') {
    this.track('signup', { method });
  }

  trackLogin(method: string = 'email') {
    this.track('login', { method });
  }

  trackProfileView(viewedUserId: number) {
    this.track('profile_view', { viewedUserId });
  }

  trackLike(likedUserId: number) {
    this.track('like_sent', { likedUserId });
  }

  trackMatch(matchedUserId: number) {
    this.track('match_made', { matchedUserId });
  }

  trackMessageSent(receiverId: number, messageLength: number) {
    this.track('message_sent', { receiverId, messageLength });
  }

  trackHashtagClick(hashtagId: number, hashtagName: string) {
    this.track('hashtag_click', { hashtagId, hashtagName });
  }

  trackHashtagAdded(hashtagId: number, hashtagName: string) {
    this.track('hashtag_added', { hashtagId, hashtagName });
  }

  trackGreenFlagAdded(flagId: number) {
    this.track('green_flag_added', { flagId });
  }

  trackProfileCompleted() {
    this.track('profile_completed');
  }

  trackPhotoUploaded() {
    this.track('photo_uploaded');
  }

  trackSubscriptionPurchased(planType: string, amount: number) {
    this.track('subscription_purchased', { planType, amount });
  }

  trackFeatureUsed(featureName: string, featureData?: any) {
    this.track('feature_used', { feature: featureName, ...featureData });
  }

  trackError(errorType: string, errorMessage: string) {
    this.track('error', { errorType, errorMessage });
  }

  trackSearch(searchType: string, query?: string) {
    this.track('search', { searchType, query });
  }

  trackShare(shareType: string, sharedContent?: string) {
    this.track('share', { shareType, sharedContent });
  }
}

// Create singleton instance
const analytics = new AnalyticsService();

export default analytics;
